// A shift's cash timeline (spec/13 §10, F-OPS-04): every event on the drawer, oldest first — the opening float, each
// cash sale with its order number (opening it in Orders), refunds, wallet top-ups, pay-ins, pay-outs and drops,
// colleagues joining and leaving a shared drawer, the closing count with its variance and the hand-over — each with its
// time, who did it, its amount and what the drawer should hold after it.
//
// The server reads the timeline from the same ledger lines the close reads, so the running balance before the count is
// the shift's expected cash; the console only lists it. "Cash" (the default) shows the drawer's events; "All tenders"
// adds counter card, counter UPI and wallet payments, which never change the balance. A cash sale says what the customer
// handed over and the change given back (ADR-0049): "Received ₹400 · change ₹250" on a ₹150 line. The kind filter
// narrows the list to sales, cash in and out, or refunds; a search narrows to one order by its number, and Download CSV
// writes what the filters show, every page of it.

import type { Money, ShiftCashEvent, ShiftCashTimelineTenders } from '@clavian/api-client';
import { formatOrderNumber } from '@clavian/domain/menu-choice';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, type BadgeTone, Button, cx, SegmentedControl, Table, type TableColumn, useToast } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { problemStatus } from '../../api/problems';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { Notice, SearchField, TableSkeleton, Toolbar, ToolbarSpring } from '../../ui';
import { saveFile } from '../audit/save-file';
import { type CashTimelineFilter, useReadWholeCashTimeline, useShiftCashTimeline } from './queries';
import { Variance } from './variance';

import './cash-timeline.css';

/** How an event reads on a badge: money in is good news, money out is neutral, the count says how it went. */
const KIND_TONE: Readonly<Partial<Record<ShiftCashEvent['kind'], BadgeTone>>> = {
  opening_float: 'accent',
  sale: 'ok',
  wallet_top_up: 'ok',
  pass_sale: 'ok',
  pay_in: 'ok',
  refund: 'warn',
  wallet_refund: 'warn',
  pass_refund: 'warn',
  closing_count: 'info',
  handed_over: 'info',
};

/** The kinds the movements filter groups: every event, the sales, cash put in or taken out by hand, and refunds. */
export type MovementFilter = 'all' | 'sales' | 'cash' | 'refunds';

const FILTER_KINDS: Readonly<Record<Exclude<MovementFilter, 'all'>, ReadonlySet<ShiftCashEvent['kind']>>> = {
  sales: new Set(['sale', 'pass_sale', 'wallet_top_up']),
  cash: new Set(['opening_float', 'pay_in', 'pay_out', 'drop', 'closing_count', 'handed_over']),
  refunds: new Set(['refund', 'pass_refund', 'wallet_refund']),
};

/** Whether an event is one the movements filter shows. */
export function matchesMovementFilter(event: Pick<ShiftCashEvent, 'kind'>, filter: MovementFilter): boolean {
  return filter === 'all' || FILTER_KINDS[filter].has(event.kind);
}

/** The order number a search box holds — "421", "#0421" — or undefined when it holds none. */
export function orderNumberOf(text: string): number | undefined {
  const digits = text.replace(/[^0-9]/g, '');
  if (digits === '') return undefined;
  const value = Number.parseInt(digits, 10);
  return Number.isSafeInteger(value) && value >= 1 && value <= 99_999 ? value : undefined;
}

/** A CSV cell: quoted when it holds a comma, a quote or a line break (RFC 4180). */
function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function CashTimeline({
  organizationId,
  venueId,
  venueSlug,
  shiftId,
  expectedCash,
  closed,
  canOpenOrders,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly venueSlug: string;
  readonly shiftId: string;
  /** The shift's expected drawer — what the balance before the count (or now, while open) comes to. */
  readonly expectedCash: Money;
  readonly closed: boolean;
  /** Whether an order number links to the order (`orders:read`). */
  readonly canOpenOrders: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const { show } = useToast();
  const [tenders, setTenders] = useState<ShiftCashTimelineTenders>('cash');
  const [kinds, setKinds] = useState<MovementFilter>('all');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const orderNumber = orderNumberOf(search);
  const filter: CashTimelineFilter = { tenders, ...(orderNumber === undefined ? {} : { orderNumber }) };
  const timeline = useShiftCashTimeline(organizationId, venueId, shiftId, filter);
  const readAll = useReadWholeCashTimeline(organizationId, venueId, shiftId);
  const events = (timeline.data?.pages.flatMap((page) => page.data) ?? []).filter((event) => matchesMovementFilter(event, kinds));

  const kindLabel = (event: ShiftCashEvent) =>
    event.kind === 'opening_float' && event.otherShiftId !== null ? t('shifts.timeline.kind.handedIn') : t(`shifts.timeline.kind.${event.kind}`);
  const orderOf = (event: ShiftCashEvent): string | null => {
    const order = formatOrderNumber(event.orderNumber);
    return order === null ? null : t('shifts.timeline.order', { number: order });
  };
  /** What else an event says beside its kind and order: a tender other than cash, the reason, the count. */
  const details = (event: ShiftCashEvent): string => {
    const parts: string[] = [];
    if (event.tender !== null && event.tender !== 'cash') parts.push(t(`shifts.tender.${event.tender}`));
    // A cash sale as the counter handled it (ADR-0049): what was handed over, and the change given back.
    if (event.cashReceived !== null && event.changeGiven !== null && event.changeGiven.amount > 0) {
      parts.push(t('shifts.timeline.handOver', { received: format.money(event.cashReceived), change: format.money(event.changeGiven) }));
    }
    if (event.reason !== null) parts.push(event.reason);
    if (event.kind === 'closing_count' && event.expected !== null && event.counted !== null) {
      parts.push(t('shifts.timeline.counted', { counted: format.money(event.counted), expected: format.money(event.expected) }));
    }
    return parts.join(' · ');
  };

  async function download(): Promise<void> {
    setExporting(true);
    try {
      const all = (await readAll(filter)).filter((event) => matchesMovementFilter(event, kinds));
      const header = [
        t('shifts.timeline.col.at'),
        t('shifts.timeline.col.event'),
        t('shifts.timeline.col.detail'),
        t('shifts.timeline.col.by'),
        t('shifts.tenderLabel'),
        t('shifts.timeline.col.received'),
        t('shifts.timeline.col.change'),
        t('shifts.timeline.col.amount'),
        t('shifts.variance'),
        t('shifts.timeline.col.balance'),
      ];
      const lines = all.map((event) =>
        [
          format.dateTime(event.at),
          kindLabel(event),
          [orderOf(event), details(event)].filter((part) => part !== null && part !== '').join(' · '),
          event.by?.name ?? event.by?.memberId ?? '',
          event.tender === null ? '' : t(`shifts.tender.${event.tender}`),
          event.cashReceived === null ? '' : format.money(event.cashReceived),
          event.changeGiven === null ? '' : format.money(event.changeGiven),
          event.amount === null ? '' : format.money(event.amount),
          event.variance === null ? '' : format.money(event.variance),
          format.money(event.balance),
        ]
          .map(csvCell)
          .join(','),
      );
      saveFile(`shift-${shiftId}-cash-timeline.csv`, [header.map(csvCell).join(','), ...lines].join('\r\n') + '\r\n', 'text/csv');
    } catch {
      show({ tone: 'danger', title: t('shifts.timeline.downloadFailed') });
    } finally {
      setExporting(false);
    }
  }

  const columns: readonly TableColumn<ShiftCashEvent>[] = [
    { key: 'at', header: t('shifts.timeline.col.at'), cell: (event) => <span className="console-num console-subtle">{format.time(event.at)}</span> },
    {
      key: 'event',
      header: t('shifts.timeline.col.event'),
      cell: (event) => {
        const order = orderOf(event);
        const rest = details(event);
        return (
          <span className="console-timeline__event">
            <Badge tone={KIND_TONE[event.kind] ?? 'neutral'} dot>
              {kindLabel(event)}
            </Badge>
            {order !== null && event.saleId !== null && canOpenOrders ? (
              <Link to="/v/$venueSlug/orders" params={{ venueSlug }} search={{ sale: event.saleId }} className="console-timeline__order">
                {order}
              </Link>
            ) : order !== null ? (
              <span className="console-timeline__order">{order}</span>
            ) : null}
            {rest === '' ? null : <span className="console-subtle">{rest}</span>}
          </span>
        );
      },
    },
    { key: 'by', header: t('shifts.timeline.col.by'), cell: (event) => (event.by === null ? <span className="console-subtle">—</span> : (event.by.name ?? t('shifts.timeline.unknown'))) },
    {
      key: 'amount',
      header: t('shifts.timeline.col.amount'),
      numeric: true,
      cell: (event) =>
        event.kind === 'closing_count' && event.variance !== null ? (
          <Variance value={event.variance} />
        ) : event.amount === null ? (
          <span className="console-subtle">—</span>
        ) : (
          <span className={cx('console-money', event.tender !== null && event.tender !== 'cash' ? 'console-timeline__outside' : event.amount.amount < 0 ? 'console-timeline__out' : 'console-timeline__in')}>
            {event.amount.amount > 0 ? '+' : ''}
            {format.money(event.amount)}
          </span>
        ),
    },
    {
      key: 'balance',
      header: t('shifts.timeline.col.balance'),
      numeric: true,
      cell: (event) =>
        event.tender !== null && event.tender !== 'cash' ? (
          <span className="console-subtle console-timeline__note">{t('shifts.timeline.notInDrawer')}</span>
        ) : (
          <span className="console-money console-timeline__balance">{format.money(event.balance)}</span>
        ),
    },
  ];

  const forbidden = timeline.isError && problemStatus(timeline.error) === 403;
  return (
    <div className="console-timeline">
      <Toolbar label={t('shifts.timeline.title')}>
        <SegmentedControl<MovementFilter>
          size="sm"
          label={t('shifts.timeline.kindFilter')}
          value={kinds}
          onValueChange={setKinds}
          options={[
            { value: 'all', label: t('shifts.timeline.kinds.all') },
            { value: 'sales', label: t('shifts.timeline.kinds.sales') },
            { value: 'cash', label: t('shifts.timeline.kinds.cash') },
            { value: 'refunds', label: t('shifts.timeline.kinds.refunds') },
          ]}
        />
        <SegmentedControl<ShiftCashTimelineTenders>
          size="sm"
          label={t('shifts.timeline.filter')}
          value={tenders}
          onValueChange={setTenders}
          options={[
            { value: 'cash', label: t('shifts.timeline.cashOnly') },
            { value: 'all', label: t('shifts.timeline.allTenders') },
          ]}
        />
        <SearchField label={t('shifts.timeline.search')} inputMode="numeric" value={search} onChange={(event) => setSearch(event.target.value)} className="console-timeline__search" />
        <ToolbarSpring />
        <Button variant="secondary" size="sm" icon={<Icon name="download" />} onClick={() => void download()} busy={exporting} disabled={forbidden || events.length === 0}>
          {t('shifts.timeline.download')}
        </Button>
      </Toolbar>
      {timeline.data === undefined ? (
        forbidden ? (
          <Notice tone="info" role="note">
            {t('shifts.timeline.notPermitted')}
          </Notice>
        ) : timeline.isError ? (
          <LoadFailure error={timeline.error} onRetry={() => void timeline.refetch()} />
        ) : (
          <TableSkeleton rows={5} />
        )
      ) : (
        <>
          <Table
            caption={t('shifts.timeline.title')}
            captionHidden
            columns={columns}
            rows={events}
            rowKey={(event) => event.id}
            empty={orderNumber !== undefined ? t('shifts.timeline.noMatch') : kinds !== 'all' ? t('shifts.timeline.noKind') : t('shifts.timeline.empty')}
          />
          {timeline.hasNextPage ? (
            <div className="console-timeline__more">
              <Button variant="ghost" size="sm" onClick={() => void timeline.fetchNextPage()} busy={timeline.isFetchingNextPage}>
                {t('shifts.timeline.more')}
              </Button>
            </div>
          ) : null}
          <p className="console-timeline__total">
            <span className="console-subtle">{closed ? t('shifts.timeline.expectedAtClose') : t('shifts.timeline.expectedNow')}</span>
            <span className="console-money">{format.money(expectedCash)}</span>
          </p>
        </>
      )}
    </div>
  );
}
